import React, { useMemo } from 'react'
import { OrderSummary } from '../../../../components/OrderSummary'
import {
  calculateTotalDiscount,
  calculateTotalGST,
  calculateDiscountedPriceIncGst,
} from '../../utils/calculateTotal'
import { addGST } from '../../../../util/addGst'
import { CartItem } from '../../../cart/storage/cart.types'
import { formatPrice } from '../../../../util/helpers'
import { Typography } from '@mui/material'
import TestIds from '../../../../testIds'
import { isEqual, pick } from 'lodash'
import { CartMessage } from '../../../cart/utils/cartMessages'

interface QuoteSummaryTotalProps {
  products: CartItem[]
  originalProducts?: CartItem[]
  accountName?: string
  accountNumber?: string
  freightChargeExcGst: number | null
  showDivider?: boolean
  title?: string
}

export const QuoteSummaryTotal = ({
  products,
  originalProducts,
  accountName,
  accountNumber,
  freightChargeExcGst,
  showDivider = true,
  title = 'Quote Summary',
}: QuoteSummaryTotalProps) => {
  const total = useMemo(
    () => formatPrice(calculateDiscountedPriceIncGst(products, freightChargeExcGst)),
    [products],
  )

  const freightMessage = useMemo(() => {
    if (!originalProducts || !freightChargeExcGst) return null
    const productQuantities = products.map((p) => pick(p, ['quantity', 'productId']))
    const originalProductQuantities = originalProducts.map((p) =>
      pick(p, ['quantity', 'productId']),
    )
    if (isEqual(productQuantities, originalProductQuantities)) return null

    return (
      <Typography variant='body2' data-testid={TestIds.feat.quotes.summary.orderChargedMessage}>
        {CartMessage.deliveryCost}
      </Typography>
    )
  }, [products, originalProducts, freightChargeExcGst])

  const gst = useMemo(() => formatPrice(calculateTotalGST(products)), [products])

  const deliveryCost = useMemo(() => {
    if (freightChargeExcGst) {
      return formatPrice(addGST(freightChargeExcGst))
    }
    return undefined
  }, [freightChargeExcGst])

  const discount = useMemo(() => {
    const discountTotal = calculateTotalDiscount(products)
    if (discountTotal > 0) {
      return formatPrice(discountTotal)
    }
    return undefined
  }, [products])

  return (
    <>
      <OrderSummary
        title={title}
        accountName={accountName}
        accountNumber={accountNumber}
        total={total}
        gst={gst}
        delivery={deliveryCost}
        discount={discount}
        showDivider={showDivider}
      />
      {freightMessage}
    </>
  )
}
